/**
 * 모던 카드 컴포넌트
 * 다양한 variant와 레이아웃을 지원하는 재사용 가능한 카드
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { colors, spacing, borderRadius, shadows, animations, components } from '../../styles/designTokens';

// ===== 타입 정의 =====
export type CardVariant = 'default' | 'elevated' | 'outlined' | 'filled';
export type CardSize = 'sm' | 'md' | 'lg' | 'xl';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: CardVariant;
    size?: CardSize;
    hoverable?: boolean;
    clickable?: boolean;
    children: React.ReactNode;
}

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
}

// ===== 스타일드 컴포넌트 =====
const CardBase = styled.div<{
    $variant: CardVariant;
    $size: CardSize;
    $hoverable: boolean;
    $clickable: boolean;
}>`
  /* 기본 스타일 */
  display: flex;
  flex-direction: column;
  border-radius: ${borderRadius.xl};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  overflow: hidden;
  
    /* 크기별 패딩 */
  ${({ $size }) => {
        switch ($size) {
            case 'sm':
                return css`
          padding: ${components.card.padding.sm};
          @media (max-width: 480px) {
            padding: ${spacing[3]};
          }
        `;
            case 'md':
                return css`
          padding: ${components.card.padding.md};
          @media (max-width: 480px) {
            padding: ${spacing[4]};
          }
        `;
            case 'lg':
                return css`
          padding: ${components.card.padding.lg};
          @media (max-width: 480px) {
            padding: ${spacing[4]};
          }
        `;
            case 'xl':
                return css`
          padding: ${components.card.padding.xl};
          @media (max-width: 480px) {
            padding: ${spacing[5]};
          }
        `;
        }
    }}
  
  /* Variant별 스타일 */
  ${({ $variant }) => {
        switch ($variant) {
            case 'default':
                return css`
          background-color: ${colors.semantic.background.primary};
          border: 1px solid ${colors.semantic.border.primary};
          box-shadow: ${shadows.sm};
        `;

            case 'elevated':
                return css`
          background-color: ${colors.semantic.background.primary};
          border: 1px solid ${colors.semantic.border.primary};
          box-shadow: ${shadows.lg};
        `;

            case 'outlined':
                return css`
          background-color: transparent;
          border: 2px solid ${colors.semantic.border.primary};
          box-shadow: none;
        `;

            case 'filled':
                return css`
          background-color: ${colors.semantic.background.tertiary};
          border: 1px solid ${colors.semantic.border.primary};
          box-shadow: none;
        `;
        }
    }}
  
  /* 호버 효과 */
  ${({ $hoverable, $variant }) => $hoverable && css`
    &:hover {
      transform: translateY(-2px);
      box-shadow: ${$variant === 'elevated' ? shadows['2xl'] : shadows.lg};
      border-color: ${colors.primary[300]};
    }
  `}
  
  /* 클릭 가능 */
  ${({ $clickable }) => $clickable && css`
    cursor: pointer;
    
    &:active {
      transform: translateY(0);
    }
  `}
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
    
    &:hover {
      transform: none !important;
    }
  }
`;

const CardHeader = styled.div`
  padding-bottom: ${spacing[4]};
  margin-bottom: ${spacing[4]};
  border-bottom: 1px solid ${colors.semantic.border.primary};
  
  h1, h2, h3, h4, h5, h6 {
    margin: 0;
    color: ${colors.semantic.text.primary};
  }
  
  p {
    margin: ${spacing[1]} 0 0 0;
    color: ${colors.semantic.text.secondary};
    font-size: 0.875rem;
  }
`;

const CardBody = styled.div`
  flex: 1;
  
  /* 마지막 요소의 하단 마진 제거 */
  > *:last-child {
    margin-bottom: 0;
  }
`;

const CardFooter = styled.div`
  padding-top: ${spacing[4]};
  margin-top: ${spacing[4]};
  border-top: 1px solid ${colors.semantic.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  
  /* 버튼 그룹 스타일 */
  .button-group {
    display: flex;
    gap: ${spacing[2]};
  }
`;

// ===== 메인 컴포넌트 =====
export const Card = forwardRef<HTMLDivElement, CardProps>(
    (
        {
            variant = 'default',
            size = 'md',
            hoverable = false,
            clickable = false,
            children,
            ...props
        },
        ref
    ) => {
        return (
            <CardBase
                ref={ref}
                $variant={variant}
                $size={size}
                $hoverable={hoverable}
                $clickable={clickable}
                {...props}
            >
                {children}
            </CardBase>
        );
    }
);

Card.displayName = 'Card';

// ===== 서브 컴포넌트들 =====
export const CardHeaderComponent = forwardRef<HTMLDivElement, CardHeaderProps>(
    ({ children, ...props }, ref) => {
        return (
            <CardHeader ref={ref} {...props}>
                {children}
            </CardHeader>
        );
    }
);

CardHeaderComponent.displayName = 'CardHeader';

export const CardBodyComponent = forwardRef<HTMLDivElement, CardBodyProps>(
    ({ children, ...props }, ref) => {
        return (
            <CardBody ref={ref} {...props}>
                {children}
            </CardBody>
        );
    }
);

CardBodyComponent.displayName = 'CardBody';

export const CardFooterComponent = forwardRef<HTMLDivElement, CardFooterProps>(
    ({ children, ...props }, ref) => {
        return (
            <CardFooter ref={ref} {...props}>
                {children}
            </CardFooter>
        );
    }
);

CardFooterComponent.displayName = 'CardFooter';

// ===== 카드 그리드 컴포넌트 =====
export const CardGrid = styled.div<{
    $columns?: number;
    $gap?: keyof typeof spacing;
    $responsive?: boolean;
}>`
  display: grid;
  gap: ${({ $gap = 6 }) => spacing[$gap]};
  
  ${({ $columns = 1, $responsive = true }) => {
        if ($responsive) {
            return css`
        /* 모바일: 1열, 최소 너비 280px */
        grid-template-columns: 1fr;
        
        @media (min-width: 480px) {
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        }
        
        @media (min-width: 640px) {
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        }
        
        @media (min-width: 768px) {
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }
        
        @media (min-width: 1024px) {
          grid-template-columns: repeat(${Math.min($columns, 3)}, 1fr);
        }
        
        @media (min-width: 1280px) {
          grid-template-columns: repeat(${Math.min($columns, 4)}, 1fr);
        }
      `;
        } else {
            return css`
        grid-template-columns: repeat(${Math.min($columns, 4)}, 1fr);
      `;
        }
    }}
  
  /* 모바일에서 카드 간격 조정 */
  @media (max-width: 480px) {
    gap: ${spacing[3]};
  }
`;

// ===== 기본 내보내기 =====
export default Card;
